import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { AddCircle, Refresh, Link } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import sharePointTokenService from '../../services/sharePointTokenService';
import authApi from '../../services/authApi';
import { sharePointService } from '../../services/sharePointService';
import { useUserSession } from '../../contexts/UserContext';

const SharePointTokenManager = () => {
  const { userSession } = useUserSession();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configurations, setConfigurations] = useState([]);
  const [tokenStatuses, setTokenStatuses] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [consentUrl, setConsentUrl] = useState('');

  // Fetch SharePoint configurations
  const fetchConfigurations = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authApi.getToken();
      
      if (!userSession?.effectiveEmail && !userSession?.email) {
        console.warn('User email is missing, cannot fetch configurations');
        setError('User email is required');
        setLoading(false);
        return;
      }
      
      console.log('Loading SharePoint config for effective user:', userSession.effectiveEmail || userSession.email);
      const response = await sharePointService.getConfig(token);
      console.log('SharePoint config loaded:', response);
      
      // Log all configurations if available
      if (response && response.all_configurations) {
        console.log('All SharePoint configurations for this user:');
        console.table(response.all_configurations);
      }
      
      // Handle different response formats
      if (response) {
        // Create a properly formatted configuration array
        let configs = [];
        
        if (response.configs && Array.isArray(response.configs)) {
          // Multiple configurations format
          configs = response.configs;
          console.log('Using configs array from response:', configs);
        } else if (response.tenant_url && response.client_id) {
          // Single configuration format
          configs = [{
            id: response.id, // Use the ID from response directly - don't default to 1
            tenant_url: response.tenant_url,
            client_id: response.client_id,
            tenant_id: response.tenant_id || '',
            additional_scopes: response.additional_scopes || ''
          }];
          console.log('Created single config object:', configs[0]);
        } else if (response.success === true && Array.isArray(response.configs)) {
          // API success response format
          configs = response.configs;
          console.log('Using configs from success response:', configs);
        }
        
        // Ensure each config has an ID - this is critical
        configs = configs.map(config => {
          if (!config.id) {
            console.error('Configuration missing ID, skipping:', config);
            return null;
          }
          return config;
        }).filter(Boolean); // Remove null entries
        
        console.log('Final processed configurations:', configs);
        setConfigurations(configs);
        
        // Fetch token status for each configuration
        const statuses = {};
        for (const config of configs) {
          try {            
            console.log(`Fetching token status for config ID: ${config.id}`);
            const statusResponse = await sharePointTokenService.getTokenStatus(token, config.id);
            statuses[config.id] = statusResponse.tokenStatus;
          } catch (err) {
            console.error(`Error fetching token status for config ${config.id}:`, err);
            statuses[config.id] = null;
          }
        }
        setTokenStatuses(statuses);
      } else {
        console.error('Invalid response format:', response);
        setError('Failed to load SharePoint configurations');
      }
    } catch (err) {
      console.error('Error loading configurations:', err);
      setError(err.message || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userSession) {
      fetchConfigurations();
    }
  }, [userSession]);

  // Generate consent URL for authorization
  const handleGenerateConsentUrl = async (config) => {
    try {
      setSelectedConfig(config);
      const token = authApi.getToken();
      
      // Ensure we have required fields before proceeding
      if (!config.client_id || !config.tenant_url || !config.tenant_id) {
        setError('Missing required configuration details: client ID, tenant URL, or tenant ID');
        return;
      }
      
      // Ensure config ID is a number (convert to number if it's a string)
      const configId = typeof config.id === 'string' ? parseInt(config.id, 10) : config.id;
      
      console.log('Creating consent URL with config:', {
        configId, // Use the numeric ID
        clientId: config.client_id,
        tenantId: config.tenant_id,
        tenantUrl: config.tenant_url,
      });
      
      const response = await sharePointTokenService.createConsentUrl(token, {
        configId, // Pass the numeric ID
        clientId: config.client_id,
        tenantId: config.tenant_id,
        tenantUrl: config.tenant_url,
        redirectUri: `${window.location.origin}/auth/callback`
      });
      
      if (response.success) {
        console.log('Generated consent URL:', response.consentUrl);
        setConsentUrl(response.consentUrl);
        setOpenDialog(true);
      } else {
        setError('Failed to generate consent URL: ' + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error generating consent URL:', err);
      setError(err.message || 'Failed to generate consent URL');
    }
  };

  // Open consent URL in a new tab
  const handleOpenConsentUrl = () => {
    // Ensure we have a valid consent URL before opening
    if (!consentUrl) {
      setError('No consent URL generated. Please try again.');
      return;
    }
    
    // Open the URL in a new tab
    window.open(consentUrl, '_blank');
    setOpenDialog(false);
  };

  // Test API connectivity
  const handleTestApiConnectivity = async (configId) => {
    try {
      // Ensure configId is a number
      const numericConfigId = typeof configId === 'string' ? parseInt(configId, 10) : configId;
      
      console.log(`Testing API connectivity for config ID: ${numericConfigId}`);
      const token = authApi.getToken();
      const response = await sharePointTokenService.testApiConnectivity(token, numericConfigId);
      
      if (response.success) {
        alert('API connectivity test successful!');
      } else {
        alert('API connectivity test failed: ' + response.message);
      }
    } catch (err) {
      console.error('Error testing API connectivity:', err);
      alert('Error testing API connectivity: ' + (err.message || 'Unknown error'));
    }
  };

  // Format expiration date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get token status display
  const getTokenStatusDisplay = (configId) => {
    const status = tokenStatuses[configId];
    console.log(`Token status for config ${configId}:`, status);
    
    if (!status) {
      return {
        color: 'error',
        text: 'No tokens available'
      };
    }
    
    // Ensure time comparison is done in UTC
    const now = new Date();
    
    // Check graph token
    if (status.graphToken && new Date(status.graphToken.expiresAt) > now) {
      // Calculate remaining time in hours and minutes
      const remainingMs = new Date(status.graphToken.expiresAt) - now;
      const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
      const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      
      return {
        color: 'success',
        text: `Active (${remainingHours}h ${remainingMinutes}m)`
      };
    }
    
    // Check SharePoint token
    if (status.sharePointToken && new Date(status.sharePointToken.expiresAt) > now) {
      // Calculate remaining time in hours and minutes
      const remainingMs = new Date(status.sharePointToken.expiresAt) - now;
      const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
      const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      
      return {
        color: 'success',
        text: `Active (${remainingHours}h ${remainingMinutes}m)`
      };
    }
    
    // Check refresh token
    if (status.refreshTokenExists) {
      return {
        color: 'warning',
        text: 'Tokens expired, refresh available'
      };
    }
    
    return {
      color: 'error',
      text: 'Tokens expired'
    };
  };

  const handleAddConfiguration = () => {
    navigate('/sharepoint-config');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          SharePoint Token Manager
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={fetchConfigurations}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      <Typography variant="body2" sx={{ mb: 3 }}>
        Manage authentication tokens for SharePoint API access. These tokens are required for retrieving document-specific permissions.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : configurations.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>
            No SharePoint configurations found.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddCircle />}
            onClick={handleAddConfiguration}
            sx={{ mt: 2 }}
          >
            Add Configuration
          </Button>
        </Paper>
      ) : (
        <>
          {/* Debug info to help troubleshoot */}
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" component="pre" sx={{ overflowX: 'auto' }}>
                Debug: userSession.email: {userSession?.email || 'undefined'}<br/>
                Configurations: {JSON.stringify(configurations.map(c => ({ id: c.id, tenant_url: c.tenant_url?.substring(0, 20) })), null, 2)}
              </Typography>
            </Box>
          )}
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant URL</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Token Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configurations.map((config) => {
                  // Ensure each config has an ID to work with
                  if (!config.id) {
                    console.error('Configuration missing ID in render:', config);
                    return null; // Skip this item in rendering
                  }
                  
                  // Get token status for this config
                  const statusDisplay = getTokenStatusDisplay(config.id);
                  
                  return (
                    <TableRow key={config.id}>
                      <TableCell>{config.tenant_url}</TableCell>
                      <TableCell>{config.client_id}</TableCell>
                      <TableCell>
                        <Alert 
                          severity={statusDisplay.color} 
                          sx={{ 
                            py: 0, 
                            display: 'inline-flex',
                            '.MuiAlert-message': { p: 0 }
                          }}
                        >
                          {statusDisplay.text}
                        </Alert>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleGenerateConsentUrl(config)}
                          sx={{ mr: 1 }}
                        >
                          Authorize
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleTestApiConnectivity(config.id)}
                        >
                          Test
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      
      {/* Consent URL Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Authorization Required</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Click the button below to open the Microsoft authorization page in a new tab.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You will need to sign in with your Microsoft account and grant the requested permissions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleOpenConsentUrl} 
            color="primary" 
            variant="contained"
            startIcon={<Link />}
          >
            Open Authorization Page
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharePointTokenManager; 